(function() {
  /**
   * @typedef {Object} RebuyCartItem
   * @property {number} quantity
   * @property {number} original_line_price
   * @property {number} final_line_price
   * @property {Object} [selling_plan_allocation]
   * @property {number} [selling_plan_allocation.compare_at_price]
   * @property {number} [selling_plan_allocation.price]
   */

  /**
   * @typedef {Object} RebuyCart
   * @property {RebuyCartItem[]} items
   * @property {string} [currency]
   */

  // Store the last calculated savings value
  let lastCalculatedSavings = {
    amount: 0,
    currency: 'USD',
    formatted: ''
  };

  /**
   * Helper function to format money from cents (e.g., 1050 -> $10.50)
   * @param {number} cents - Price in cents
   * @param {string} [currencyCode] - Currency code (default: 'USD')
   * @returns {string} Formatted currency string
   */
  function formatRebuyMoney(cents, currencyCode) {
    const value = (cents / 100).toFixed(2);
    try {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
      });
      return formatter.format(parseFloat(value));
    } catch (e) {
      // Fallback for unknown currency codes
      return '$' + value;
    }
  }

  /**
   * Calculates total line-item savings by looping through all cart items.
   * @param {RebuyCartItem[]} cartItems - Array of cart items
   * @returns {number} Total savings in cents
   */
  function calculateLineItemSavings(cartItems) {
    if (!cartItems || cartItems.length === 0) {
      return 0;
    }

    // Use .reduce() to sum up savings from all items
    return cartItems.reduce((total, item) => {
      let itemSavings = 0;

      // Case 1: Item is a subscription
      if (item.selling_plan_allocation) {
        const compareAt = item.selling_plan_allocation.compare_at_price;
        const finalPrice = item.selling_plan_allocation.price;

        if (compareAt && finalPrice && compareAt > finalPrice) {
          itemSavings = (compareAt - finalPrice) * item.quantity;
        }
      }
      // Case 2: Item is a one-time purchase on sale
      else if (item.original_line_price > item.final_line_price) {
        itemSavings = item.original_line_price - item.final_line_price;
      }

      return total + itemSavings;
    }, 0);
  }


  /**
   * Recursively finds elements in a root and all its descendant shadow roots.
   * @param {Document|ShadowRoot} root - The root to search within.
   * @param {Set<Element>} resultsSet - A Set to add found elements to (handles deduplication).
   * @param {string} selector - The selector to search for
   */
  function findElements(root, resultsSet, selector) {
    // Find elements in the current root
    root.querySelectorAll(selector).forEach(el => resultsSet.add(el));

    // Find all elements that have a shadow root and recurse
    root.querySelectorAll('*').forEach(el => {
      if (el.shadowRoot) {
        findElements(el.shadowRoot, resultsSet, selector);
      }
    });
  }

  /**
   * Finds all savings elements, including in shadow DOMs and iframes.
   * @returns {Element[]} Array of unique elements
   */
  function findAllSavingsElements() {
    const resultsSet = new Set();

    // Search main document and its shadow roots
    findElements(document, resultsSet, '[data-td-savings]');

    // Search iframes and their shadow roots
    try {
      document.querySelectorAll('iframe').forEach((iframe) => {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          findElements(iframeDoc, resultsSet, '[data-td-savings]');
        }
      });
    } catch (e) {
      // Silently fail on cross-origin iframes
    }

    return Array.from(resultsSet);
  }

  /**
   * Finds all container elements, including in shadow DOMs and iframes.
   * @returns {Element[]} Array of unique elements
   */
  function findAllContainerElements() {
    const resultsSet = new Set();

    // Search main document and its shadow roots
    findElements(document, resultsSet, '[data-td-savings-container]');

    // Search iframes and their shadow roots
    try {
      document.querySelectorAll('iframe').forEach((iframe) => {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          findElements(iframeDoc, resultsSet, '[data-td-savings-container]');
        }
      });
    } catch (e) {
      // Silently fail on cross-origin iframes
    }

    return Array.from(resultsSet);
  }

  /**
   * Helper function to update all found elements with the savings value.
   * @param {string} formattedSavings - The formatted string (e.g., "$10.50")
   * @param {number} amount - The savings amount in cents
   */
  function _updateAllElements(formattedSavings, amount) {
    // Update savings display elements
    const savingsSpans = findAllSavingsElements();
    savingsSpans.forEach((span) => {
      const element = /** @type {HTMLElement} */ (span);
      if (amount > 0) {
        element.innerHTML = formattedSavings;
        element.style.display = 'inline';
      } else {
        element.innerHTML = '';
        element.style.display = 'none';
      }
    });

    // Update container visibility
    const containers = findAllContainerElements();
    containers.forEach((container) => {
      const element = /** @type {HTMLElement} */ (container);
      if (amount > 0) {
        element.style.display = '';
      } else {
        element.style.display = 'none';
      }
    });
  }

  /**
   * Calculates savings from the cart and updates all display elements.
   * @param {RebuyCart} cart - The Rebuy cart object
   */
  function updateSavingsDisplay(cart) {
    if (!cart || !cart.items) {
      return;
    }

    // 1. Calculate savings
    const totalLineItemSavings = calculateLineItemSavings(cart.items);
    const currencyCode = cart.currency || 'USD';
    const formattedSavings = formatRebuyMoney(totalLineItemSavings, currencyCode);

    // 2. Store the calculated savings for the MutationObserver
    lastCalculatedSavings = {
      amount: totalLineItemSavings,
      currency: currencyCode,
      formatted: formattedSavings
    };

    // 3. Find and update all existing elements
    _updateAllElements(formattedSavings, totalLineItemSavings);
  }

  /**
   * Main event listener for Rebuy cart changes.
   */
  document.addEventListener('rebuy:cart.change', (event) => {
    try {
      const customEvent = /** @type {CustomEvent} */ (event);
      let smartcart = customEvent.detail?.cart?.cart;
      if (smartcart) {
        updateSavingsDisplay(smartcart);
      }
    } catch (e) {
      console.error("Rebuy Savings Error:", e);
    }
  });

  /**
   * Also listen for smartcart ready/update events as fallback
   */
  document.addEventListener('rebuy:smartcart.ready', () => {
    try {
      const rebuy = /** @type {any} */ (window).Rebuy;
      if (rebuy && rebuy.smartcart && rebuy.smartcart.cart) {
        updateSavingsDisplay(rebuy.smartcart.cart);
      }
    } catch (e) {
      console.error("Rebuy Savings Ready Error:", e);
    }
  });

  /**
   * Sets up MutationObserver to watch for when savings elements are added to DOM
   */
  function setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      if (lastCalculatedSavings.amount <= 0) {
        return; // No savings to show, do nothing
      }

      // Check if any added node *is* or *contains* our target element
      const hasTargetAdded = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(node =>
          node.nodeType === Node.ELEMENT_NODE &&
          (
            /** @type {Element} */ (node).hasAttribute('data-td-savings') ||
            /** @type {Element} */ (node).querySelector('[data-td-savings]')
          )
        )
      );

      if (hasTargetAdded) {
        // Update all elements, which will catch the new ones
        _updateAllElements(lastCalculatedSavings.formatted, lastCalculatedSavings.amount);
      }
    });

    // Start observing the document body for changes
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    } else {
      // Fallback if body isn't ready yet
      document.addEventListener('DOMContentLoaded', () => {
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      });
    }
  }

  /**
   * Initial check on load in case cart is already available
   */
  function initialCheck() {
    try {
      const rebuy = /** @type {any} */ (window).Rebuy;
      if (rebuy && rebuy.smartcart && rebuy.smartcart.cart) {
        updateSavingsDisplay(rebuy.smartcart.cart);
      }
    } catch (e) {
      // It's okay if this fails, listeners will catch it later
    }
  }

  /**
   * Manual test function - can be called from console to debug
   * Usage: window.tdRebuySavingsTest()
   */
  function manualTest() {
    if (lastCalculatedSavings.amount > 0) {
      _updateAllElements(lastCalculatedSavings.formatted, lastCalculatedSavings.amount);
    }
  }

  // Expose test function to window for debugging
  ( /** @type {any} */ (window)).tdRebuySavingsTest = manualTest;

  // Run initial check and set up observer
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialCheck();
      setupMutationObserver();
    });
  } else {
    initialCheck();
    setupMutationObserver();
  }

})();

/**
 * A custom element that wraps the Rebuy widget and manages its visibility
 * based on Rebuy smartcart events. Shows the widget on mobile only,
 * positioned to the left of the cart drawer, centered on the overlay.
 */
class RebuyWidgetWrapperComponent extends HTMLElement {
  #handleShow = () => {
    this.classList.add('is-visible');
  };

  #handleHide = () => {
    this.classList.remove('is-visible');
  };

  connectedCallback() {
    // Listen for Rebuy smartcart events
    document.addEventListener('rebuy:smartcart.show', this.#handleShow);
    document.addEventListener('rebuy:smartcart.hide', this.#handleHide);
  }

  disconnectedCallback() {
    // Clean up event listeners
    document.removeEventListener('rebuy:smartcart.show', this.#handleShow);
    document.removeEventListener('rebuy:smartcart.hide', this.#handleHide);
  }
}

if (!customElements.get('rebuy-widget-wrapper-component')) {
  customElements.define('rebuy-widget-wrapper-component', RebuyWidgetWrapperComponent);
}

